import { useCallback, useEffect, useMemo, useState } from 'react'
import { Accordion, Box, Collapse, Group, Paper, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconClock, IconPackage, IconTool } from '@tabler/icons-react'
import { ProjectGuideStep } from './ProjectGuideStep'
import { SparkleIndicator } from './SparkleIndicator'
import type { TemplateSection, TemplateStep } from '../types'

interface ToolOwnership {
  owned: Array<string>
  needed: Array<{ name: string; estimatedCost?: number }>
}

interface ProjectGuideStepsProps {
  steps: Array<TemplateStep>
  sections?: Array<TemplateSection>
  templateSlug: string
  projectSlug?: string
  modifiedStepSlugs?: Set<string>
  defaultExpandedStep?: number
  toolOwnership?: ToolOwnership
  isStepCompleted?: (stepIndex: number) => boolean
  onStepComplete?: (stepIndex: number, completed: boolean) => Promise<void>
  isStepUpdating?: (stepIndex: number) => boolean
}

// Compute section aggregates from steps
interface SectionAggregates {
  totalMinutes: number
  toolCount: number
  materialCount: number
  isModified: boolean
}

function computeSectionAggregates(
  section: TemplateSection,
  steps: Array<TemplateStep>,
  modifiedStepSlugs?: Set<string>
): SectionAggregates {
  const sectionSteps = steps.filter(s => s.sectionSlug === section.slug)

  const allTools = new Set<string>()
  const allMaterials = new Set<string>()

  for (const step of sectionSteps) {
    step.tools?.forEach(t => {
      const name = t.name ?? t.customName
      if (name) allTools.add(name)
    })
    step.materials?.forEach(m => {
      const name = m.name ?? m.customName
      if (name) allMaterials.add(name)
    })
  }

  return {
    totalMinutes: sectionSteps.reduce((sum, s) => sum + (s.estimatedMinutes ?? 0), 0),
    toolCount: allTools.size,
    materialCount: allMaterials.size,
    isModified: sectionSteps.some(s => modifiedStepSlugs?.has(s.slug)),
  }
}

// Format minutes as hours or minutes
function formatTime(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }
  return `${minutes}m`
}

export function ProjectGuideSteps({
  steps,
  sections,
  templateSlug,
  projectSlug: _projectSlug,
  modifiedStepSlugs,
  defaultExpandedStep = 0,
  toolOwnership: _toolOwnership,
  isStepCompleted,
  onStepComplete,
  isStepUpdating,
}: ProjectGuideStepsProps) {
  const [expandedSteps, setExpandedSteps] = useState<Array<string>>(() => {
    return [`step-${defaultExpandedStep + 1}`]
  })

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const defaultCollapsed = new Set(
      (sections ?? []).filter(s => s.defaultCollapsed).map(s => s.slug)
    )

    if (typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem(`diy_sections_${templateSlug}`)
        if (stored) {
          const parsed = JSON.parse(stored) as Array<string>
          return new Set(parsed)
        }
      } catch {
        // Ignore parse errors
      }
    }

    return defaultCollapsed
  })

  const groupedSteps = useMemo(() => {
    if (!sections || sections.length === 0) {
      const sortedSteps = [...steps].sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      return [{ section: null, steps: sortedSteps }]
    }

    const sortedSections = [...sections].sort((a, b) => a.order - b.order)

    const groups: Array<{ section: TemplateSection | null; steps: Array<TemplateStep> }> = []

    for (const section of sortedSections) {
      const sectionSteps = steps
        .filter(s => s.sectionSlug === section.slug)
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      if (sectionSteps.length > 0) {
        groups.push({ section, steps: sectionSteps })
      }
    }

    const orphanSteps = steps
      .filter(s => !s.sectionSlug)
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    if (orphanSteps.length > 0) {
      groups.push({ section: null, steps: orphanSteps })
    }

    return groups
  }, [steps, sections])

  const stepListKey = useMemo(() => steps.map(s => s.slug).join('\0'), [steps])

  const toggleSection = useCallback((sectionSlug: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionSlug)) {
        next.delete(sectionSlug)
      } else {
        next.add(sectionSlug)
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.setItem(
        `diy_sections_${templateSlug}`,
        JSON.stringify(Array.from(collapsedSections))
      )
    } catch {
      // Ignore storage errors
    }
  }, [collapsedSections, templateSlug])

  /** Reset open steps when switching guides or when the step list structure changes (e.g. personalization). */
  useEffect(() => {
    setExpandedSteps([`step-${defaultExpandedStep + 1}`])
  }, [templateSlug, stepListKey, defaultExpandedStep])

  const handleAccordionChange = useCallback((value: Array<string>) => {
    setExpandedSteps(value)
  }, [])

  let cumulativeStepIndex = 0

  return (
    <Paper p={{ base: 'sm', sm: 'lg' }} withBorder radius="md">
      <Stack gap="md">
        <Title order={2} size="h3">
          Step-by-Step Plan
        </Title>

        <Stack gap="lg">
          {groupedSteps.map(({ section, steps: sectionSteps }) => {
            const startIndex = cumulativeStepIndex
            cumulativeStepIndex += sectionSteps.length

            if (!section) {
              return (
                <Accordion
                  key="no-section"
                  variant="separated"
                  radius="md"
                  multiple
                  value={expandedSteps}
                  onChange={handleAccordionChange}
                >
                  {sectionSteps.map((step, localIndex) => {
                    const globalIndex = startIndex + localIndex
                    return (
                      <div
                        key={step.slug || globalIndex}
                        id={`step-${globalIndex}`}
                        data-toc-section
                      >
                        <ProjectGuideStep
                          step={step}
                          index={globalIndex}
                          isModified={modifiedStepSlugs?.has(step.slug)}
                          isAccordionOpen={expandedSteps.includes(`step-${globalIndex + 1}`)}
                          completed={isStepCompleted?.(globalIndex)}
                          onComplete={onStepComplete ? (completed) => onStepComplete(globalIndex, completed) : undefined}
                          isUpdating={isStepUpdating?.(globalIndex)}
                        />
                      </div>
                    )
                  })}
                </Accordion>
              )
            }

            const aggregates = computeSectionAggregates(section, steps, modifiedStepSlugs)
            const isCollapsed = collapsedSections.has(section.slug)

            return (
              <Box key={section.slug}>
                <UnstyledButton
                  onClick={() => toggleSection(section.slug)}
                  w="100%"
                  mb={isCollapsed ? 0 : 'sm'}
                >
                  <Paper
                    p={{ base: 'sm', sm: 'md' }}
                    withBorder
                    radius="md"
                    style={{
                      borderLeftWidth: 4,
                      borderLeftColor: aggregates.isModified
                        ? 'var(--mantine-color-primary-6)'
                        : 'var(--mantine-color-gray-4)',
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="sm" wrap="nowrap">
                        {section.icon && (
                          <Text size="xl">{section.icon}</Text>
                        )}
                        <Box>
                          <Group gap="xs">
                            <Text fw={600} size="lg">
                              {section.title}
                            </Text>
                            <SparkleIndicator
                              visible={aggregates.isModified}
                              persistent
                              size="md"
                              ariaLabel="Section customized"
                            />
                          </Group>
                          {section.description && (
                            <Text size="sm" c="dimmed" lineClamp={1}>
                              {section.description}
                            </Text>
                          )}
                        </Box>
                      </Group>

                      <Group gap="md" wrap="nowrap">
                        <Group gap="xs" visibleFrom="sm">
                          <IconClock size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="sm" c="dimmed">
                            {formatTime(aggregates.totalMinutes)}
                          </Text>
                        </Group>
                        {aggregates.toolCount > 0 && (
                          <Group gap="xs" visibleFrom="sm">
                            <IconTool size={14} color="var(--mantine-color-orange-6)" />
                            <Text size="sm" c="dimmed">
                              {aggregates.toolCount}
                            </Text>
                          </Group>
                        )}
                        {aggregates.materialCount > 0 && (
                          <Group gap="xs" visibleFrom="sm">
                            <IconPackage size={14} color="var(--mantine-color-teal-6)" />
                            <Text size="sm" c="dimmed">
                              {aggregates.materialCount}
                            </Text>
                          </Group>
                        )}

                        {isCollapsed ? (
                          <IconChevronDown size={20} color="var(--mantine-color-dimmed)" />
                        ) : (
                          <IconChevronUp size={20} color="var(--mantine-color-dimmed)" />
                        )}
                      </Group>
                    </Group>
                  </Paper>
                </UnstyledButton>

                <Collapse in={!isCollapsed}>
                  <Accordion
                    variant="separated"
                    radius="md"
                    multiple
                    value={expandedSteps}
                    onChange={handleAccordionChange}
                    ml={{ base: 0, sm: 'md' }}
                  >
                    {sectionSteps.map((step, localIndex) => {
                      const globalIndex = startIndex + localIndex
                      return (
                        <div
                          key={step.slug || globalIndex}
                          id={`step-${globalIndex}`}
                          data-toc-section
                        >
                          <ProjectGuideStep
                            step={step}
                            index={globalIndex}
                            isModified={modifiedStepSlugs?.has(step.slug)}
                            isAccordionOpen={expandedSteps.includes(`step-${globalIndex + 1}`)}
                          />
                        </div>
                      )
                    })}
                  </Accordion>
                </Collapse>
              </Box>
            )
          })}
        </Stack>
      </Stack>
    </Paper>
  )
}
